import { chunkedFileUpload } from '../chunkedFileUpload';

declare const window: Window & { toastr: any; ifunds: any; $: any };

let activeUploads = 0;

function reparseFormValidation(form: HTMLFormElement | null): void {
  if (!form || !window.$) {
    return;
  }
  const $ = window.$;
  const $form = $(form);
  $form.removeData('validator');
  $form.removeData('unobtrusiveValidation');
  $.validator.unobtrusive.parse(form);
  // ValidationManager (see validationManager.ts) caches required-rule object references
  // at construction. Because we just rebuilt the validator, those references are stale -
  // re-init so Save/SaveAndClose's disableRequiredRules mutates the live rule objects.
  const validationManager = $form.data('validationManager');
  if (validationManager && typeof validationManager.init === 'function') {
    validationManager.init();
  }
}

// The trash icon only marks an attachment for deletion (see main.ts): it adds the
// marked-for-deletion class and puts the field name in the _deleteAttachment input, which the
// server acts on when the form is submitted. Uploading a new file replaces that attachment
// server-side, so the mark has to be cleared. Left in place it strikes through the fresh file,
// keeps its delete icon hidden, and makes the server delete the file that was just uploaded.
function clearPendingDeletion(attachmentDiv: Element): void {
  attachmentDiv.classList.remove('marked-for-deletion');
  const deleteInput = attachmentDiv.querySelector<HTMLInputElement>('input[name="_deleteAttachment"]');
  if (deleteInput) {
    deleteInput.value = '';
  }
}

function createProgressBar(container: HTMLElement): HTMLDivElement {
  let progressWrapper = container.querySelector<HTMLDivElement>('.upload-progress');
  if (!progressWrapper) {
    progressWrapper = document.createElement('div');
    progressWrapper.className = 'upload-progress mt-1';
    progressWrapper.innerHTML = '<div class="progress" style="height: 6px;"><div class="progress-bar bg-primary" role="progressbar" style="width: 0%"></div></div>';
    container.appendChild(progressWrapper);
  }
  progressWrapper.style.display = 'block';
  const bar = progressWrapper.querySelector<HTMLDivElement>('.progress-bar')!;
  bar.style.width = '0%';
  return progressWrapper;
}

export function initPortalFileUpload(uploadUrl: string): void {
  document.querySelectorAll<HTMLElement>('.document-attachment').forEach(container => {
    const fileInput = container.querySelector<HTMLInputElement>('input[type="file"]');
    if (!fileInput || !fileInput.dataset.fieldId) {
      return;
    }

    // Cache the localized "required" message so we can restore the validation rule after
    // the user deletes an uploaded attachment. After upload we strip data-val-required, so
    // without this cache we would lose the message.
    if (fileInput.dataset.valRequired && !fileInput.dataset.requiredMessage) {
      fileInput.dataset.requiredMessage = fileInput.dataset.valRequired;
    }

    fileInput.addEventListener('change', async () => {
      const file = fileInput.files?.[0];
      if (!file) {
        return;
      }

      const fieldId = fileInput.dataset.fieldId!;
      const applicationId = fileInput.dataset.applicationId!;
      const applicationRequestId = fileInput.dataset.applicationRequestId;
      const form = fileInput.closest('form') as HTMLFormElement;

      // Check file size against max if specified
      const maxSize = fileInput.dataset.valFilesizeMaxsize;
      if (maxSize && file.size > parseInt(maxSize, 10)) {
        const errorMsg = fileInput.dataset.valFilesize || 'File is too large';
        window.toastr.error(errorMsg);
        fileInput.value = '';
        return;
      }

      activeUploads++;

      const progressWrapper = createProgressBar(container);
      const fileNameSpan = container.querySelector<HTMLSpanElement>('.selected-document-file-name');

      try {
        const response = await chunkedFileUpload(
          file,
          uploadUrl,
          (progress) => {
            const bar = progressWrapper.querySelector<HTMLDivElement>('.progress-bar')!;
            bar.style.width = `${Math.round(progress)}%`;
          },
          undefined,
          {
            extraFields: {
              fieldId: fieldId,
              applicationId: applicationId,
              ...(applicationRequestId ? { applicationRequestId } : {})
            }
          }
        );

        if (response && response.success) {
          // Show the uploaded file name
          if (fileNameSpan) {
            fileNameSpan.textContent = file.name;
          }

          // The attachment is already saved on the server, so clear the required validation.
          // Strip all data-val-* attributes and force jQuery Unobtrusive Validation to re-parse
          // the form - otherwise cached rules keep the file input "required" after its value is
          // cleared, blocking the form submit with "Required" against the field name.
          fileInput.removeAttribute('data-val');
          fileInput.removeAttribute('data-val-required');
          fileInput.removeAttribute('data-val-filesize');
          fileInput.removeAttribute('data-val-filesize-maxsize');
          fileInput.value = '';
          reparseFormValidation(form);

          const validationSpan = container.parentElement?.querySelector<HTMLSpanElement>(`[data-valmsg-for="${fieldId}"]`);
          if (validationSpan) {
            validationSpan.textContent = '';
            validationSpan.className = 'field-validation-valid';
          }

          // Clear the inline file name since it will be shown as a link below
          if (fileNameSpan) {
            fileNameSpan.textContent = '';
          }

          // Add file icon and delete button to the attachment container
          const attachmentDiv = container.closest('.attachment');
          if (attachmentDiv) {
            clearPendingDeletion(attachmentDiv);

            // Remove any existing file link and delete icon (in case of re-upload)
            attachmentDiv.querySelector('a.file')?.remove();
            attachmentDiv.querySelector('.delete-attachment')?.remove();

            // Ensure hidden input for delete marking exists
            if (!attachmentDiv.querySelector('input[name="_deleteAttachment"]')) {
              const hiddenInput = document.createElement('input');
              hiddenInput.type = 'hidden';
              hiddenInput.name = '_deleteAttachment';
              attachmentDiv.appendChild(hiddenInput);
            }

            // Build download URL
            const downloadUrl = applicationRequestId
              ? `/Application/${applicationId}/ApplicationRequest/${applicationRequestId}/Download?attachment=${encodeURIComponent(fieldId)}`
              : `/Application/${applicationId}/Download?attachment=${encodeURIComponent(fieldId)}`;

            // Determine CSS class from file extension
            const ext = response.fileName.split('.').pop()?.toLowerCase();
            const cssClass = ext ? `file-${ext}` : '';

            // Create file link with icon
            const fileLink = document.createElement('a');
            fileLink.href = downloadUrl;
            fileLink.className = `file ${cssClass}`;
            fileLink.target = '_blank';
            fileLink.textContent = response.fileName;
            attachmentDiv.appendChild(fileLink);

            // Create delete icon
            attachmentDiv.appendChild(document.createTextNode(' '));
            const deleteIcon = document.createElement('i');
            deleteIcon.className = 'fas fa-remove delete-attachment';
            attachmentDiv.appendChild(deleteIcon);
          }
        } else if (response && !response.success) {
          window.toastr.error(response.error || 'Upload failed');
          fileInput.value = '';
        }
      } catch (error: any) {
        window.toastr.error(error.message || 'Upload failed');
        fileInput.value = '';
      } finally {
        progressWrapper.style.display = 'none';
        activeUploads--;
        if (activeUploads <= 0) {
          activeUploads = 0;
        }
      }
    });
  });

  // When the user clicks the trash icon on an uploaded attachment, restore the required
  // validation rule client-side so the "Required" error is displayed on the first submit
  // click. Without this, the first submit round-trips to the server (which re-renders the
  // form with validation restored) and only the second click shows the inline error.
  window.$(document).on('click', '.delete-attachment', function (this: HTMLElement) {
    const attachmentDiv = this.closest('.attachment');
    const fileInput = attachmentDiv?.querySelector<HTMLInputElement>('input[type="file"]');
    if (!fileInput || !fileInput.dataset.fieldId) {
      return;
    }
    const requiredMessage = fileInput.dataset.requiredMessage;
    if (!requiredMessage) {
      return;
    }
    fileInput.setAttribute('data-val', 'true');
    fileInput.setAttribute('data-val-required', requiredMessage);
    reparseFormValidation(fileInput.closest('form'));
  });

  // Intercept form submit to wait for pending uploads
  document.querySelectorAll<HTMLFormElement>('form').forEach(form => {
    form.addEventListener('submit', (e) => {
      if (activeUploads > 0) {
        e.preventDefault();
        e.stopPropagation();
        window.toastr.warning(window.ifunds.strings.pleaseWaitForFileUpload);
      }
    });
  });
}
